// @ts-nocheck
// app/courses/index.tsx — Editorial courses directory (all categories)

import Head from 'expo-router/head';
import { useCallback, useMemo, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, Text, TextInput, View } from 'react-native';
import { useRouter } from 'expo-router';
import { ArrowLeft, Search } from 'lucide-react-native';

import { theme, ACCENT, INK, INK_FAINT, INK_MUTED, INK_SOFT, PAPER, PAPER_SOFT } from '@/constants/theme';
import {
  DoubleRule, EditorialButton, EditorialFooter, Eyebrow, Headline, Numeral, Paper, PageHero, Section, Tag, Title,
  useEditorialBreakpoint,
} from '@/components/editorial';
import { EditorialHeader } from '@/components/editorial/EditorialHeader';
import { AccessibilityButton } from '@/components/AccessibilityButton';
import { trackButtonClick } from '@/utils/analytics';

const MONO = theme.fonts.mono;
const SERIF = theme.fonts.serif;
const SERIF_BLACK = theme.fonts.serifBlack;
const SERIF_BOLD = theme.fonts.serifBold;
const SANS = theme.fonts.regular;
const SANS_MED = theme.fonts.medium;
const SANS_BOLD = theme.fonts.bold;
const IS_WEB = Platform.OS === 'web';

type Category = {
  key: string;
  title: string;
  subtitle: string;
  bullets: string[];
  href: string;
  chatSeed: string;
  badge?: string;
  tags: string[];
  rank: number;
  difficulty: 'מתחילים' | 'ביניים' | 'מתקדמים';
  timeToValue: 'מהיר' | 'בינוני' | 'ארוך';
  risk: 'נמוך' | 'בינוני' | 'גבוה';
};

const CATEGORIES: Category[] = [
  { key: 'programming', title: 'תכנות', subtitle: 'מסלולים למתחילים, פרויקטים ותיק עבודות — בלי הבטחות שווא.', bullets: ['בחירת מסלול לפי מטרה','תרגול אמיתי + משוב','מה הופך קורס לחלש'], href: '/courses/programming', chatSeed: 'קורס תכנות', badge: 'קריירה', tags: ['קריירה','מתחילים','תיק עבודות'], rank: 1, difficulty: 'מתחילים', timeToValue: 'ארוך', risk: 'בינוני' },
  { key: 'stock-market', title: 'שוק ההון', subtitle: 'מסחר, השקעות וניהול סיכונים — עם צ׳ק־ליסט החלטה ברור.', bullets: ['למי זה מתאים','מה לבדוק לפני תשלום','דגלים אדומים נפוצים'], href: '/courses/stock-market', chatSeed: 'קורס שוק ההון', badge: 'פופולרי', tags: ['קריירה','מתחילים','פרקטיקה'], rank: 2, difficulty: 'מתחילים', timeToValue: 'בינוני', risk: 'בינוני' },
  { key: 'crypto', title: 'קריפטו', subtitle: 'מסחר, ארנקים ובטיחות — להפריד בין ידע אמיתי לשיווק.', bullets: ['סיכונים ובטיחות','תכנית תרגול אמיתית','איך לזהות חלומות'], href: '/courses/crypto', chatSeed: 'קורס קריפטו', badge: 'חם', tags: ['מתחילים','סיכון','פרקטיקה'], rank: 3, difficulty: 'מתחילים', timeToValue: 'מהיר', risk: 'גבוה' },
  { key: 'digital-marketing', title: 'שיווק דיגיטלי', subtitle: 'קמפיינים, מדידה ופרקטיקה — להבין מה באמת לומדים.', bullets: ['PPC / SEO / סושיאל','GA4 ו-Events','תוצרים: קמפיין + KPI'], href: '/courses/digital-marketing', chatSeed: 'קורס שיווק דיגיטלי', tags: ['קריירה','פרקטיקה','מדידה'], rank: 4, difficulty: 'מתחילים', timeToValue: 'מהיר', risk: 'נמוך' },
  { key: 'qa', title: 'QA / בדיקות', subtitle: 'בדיקות ידניות ואוטומציה — מסלול כניסה טוב לטק.', bullets: ['Manual מול Automation','תרגול על מוצר אמיתי','מה לשים בקורות חיים'], href: '/courses/qa', chatSeed: 'קורס QA', tags: ['קריירה','מתחילים','פרקטיקה'], rank: 5, difficulty: 'מתחילים', timeToValue: 'בינוני', risk: 'נמוך' },
  { key: 'devops-cloud', title: 'DevOps / Cloud', subtitle: 'AWS/GCP, CI/CD ופרקטיקות תפעול — מסלול שמייצר יכולת.', bullets: ['פרויקט אמיתי','תשתיות ואוטומציה','כניסה לג׳וניור'], href: '/courses/devops-cloud', chatSeed: 'קורס DevOps Cloud', badge: 'קריירה', tags: ['קריירה','טכני','פרקטיקה'], rank: 6, difficulty: 'ביניים', timeToValue: 'ארוך', risk: 'בינוני' },
  { key: 'cyber', title: 'סייבר', subtitle: 'הגנת מידע, רשתות ומעבדות — בלי אשליות של חודש.', bullets: ['מה חובה ללמוד קודם','מעבדות ותרגול','מסלול ריאלי לג׳וניור'], href: '/courses/cyber', chatSeed: 'קורס סייבר', badge: 'קריירה', tags: ['קריירה','טכני','מעבדות'], rank: 7, difficulty: 'ביניים', timeToValue: 'ארוך', risk: 'בינוני' },
  { key: 'product', title: 'ניהול מוצר', subtitle: 'מוצר, אסטרטגיה ומחקר — לצאת עם תוצרים, לא רק מושגים.', bullets: ['PRD + Roadmap','User Research','Case study אמיתי'], href: '/courses/product', chatSeed: 'קורס ניהול מוצר', badge: 'מבוקש', tags: ['קריירה','תיק עבודות'], rank: 8, difficulty: 'ביניים', timeToValue: 'בינוני', risk: 'בינוני' },
  { key: 'ui-ux', title: 'UI / UX', subtitle: 'עיצוב מוצר ותיק עבודות — להתמקד בתוצרים, לא בסיסמאות.', bullets: ['קייס סטאדי מלא','משוב אמיתי','Figma + יסודות מוצר'], href: '/courses/ui-ux', chatSeed: 'קורס UI UX', tags: ['קריירה','תיק עבודות','פרקטיקה'], rank: 9, difficulty: 'מתחילים', timeToValue: 'בינוני', risk: 'נמוך' },
  { key: 'sales', title: 'מכירות', subtitle: 'BDR/SDR, תסריטים ותרגול — להפוך ידע ליכולת.', bullets: ['תסריטים ותרגול שיחות','תיק מכירות','מסגרת ליווי'], href: '/courses/sales', chatSeed: 'קורס מכירות', tags: ['קריירה','פרקטיקה','ליווי'], rank: 10, difficulty: 'מתחילים', timeToValue: 'מהיר', risk: 'נמוך' },
  { key: 'data-ai', title: 'דאטה / AI', subtitle: 'אנליטיקה, SQL, Python ופרויקטים — מסלול ברור.', bullets: ['BI / DS / ML','פרויקט עם דאטה אמיתי','תיק שמעסיק מבין'], href: '/courses/data-ai', chatSeed: 'קורס דאטה AI', badge: 'קריירה', tags: ['קריירה','טכני','תיק עבודות'], rank: 11, difficulty: 'ביניים', timeToValue: 'ארוך', risk: 'בינוני' },
  { key: 'automation-nocode', title: 'אוטומציה / No-Code', subtitle: 'n8n, Zapier, Airtable — תהליכים עם תוצאה.', bullets: ['Leads / CRM אמיתי','אינטגרציות','איך להימנע מבלגן'], href: '/courses/automation', chatSeed: 'קורס אוטומציה', tags: ['פרקטיקה','עסקים','תהליכים'], rank: 12, difficulty: 'מתחילים', timeToValue: 'מהיר', risk: 'נמוך' },
  { key: 'academic-paths', title: 'מסלולים אקדמיים', subtitle: 'תואר, מכינה או קורס? להבין מה הדרך הנכונה.', bullets: ['מסלול לפי יעד','זמן/עלות/תמורה','איפה נופלים'], href: '/courses/academic', chatSeed: 'מסלול אקדמי', badge: 'חדש', tags: ['אקדמיה','השוואה','תכנון'], rank: 13, difficulty: 'מתחילים', timeToValue: 'ארוך', risk: 'נמוך' },
  { key: 'finance-accounting', title: 'פיננסים והנהלת חשבונות', subtitle: 'יסודות, ניתוחים וכלים — מסלול פרקטי לשוק.', bullets: ['מה לומדים בפועל','כלים ותוכנות','למי זה רלוונטי'], href: '/courses/finance', chatSeed: 'קורס פיננסים', tags: ['קריירה','מתחילים','פרקטיקה'], rank: 14, difficulty: 'מתחילים', timeToValue: 'בינוני', risk: 'נמוך' },
  { key: 'english', title: 'אנגלית מקצועית', subtitle: 'דיבור, מיילים, ראיונות — שיפור מדיד, לא תחושה.', bullets: ['מדידה והתקדמות','תרגול דיבור','מיקוד לפי תחום'], href: '/courses/english', chatSeed: 'אנגלית מקצועית', tags: ['מתחילים','קריירה','שיפור'], rank: 15, difficulty: 'מתחילים', timeToValue: 'מהיר', risk: 'נמוך' },
  { key: 'psychometric', title: 'פסיכומטרי ומכינות', subtitle: 'שגרה, סימולציות ומעקב — המסגרת שמייצרת תוצאות.', bullets: ['כמה סימולציות','ניתוח טעויות','תכנית שבועית'], href: '/courses/psychometric', chatSeed: 'פסיכומטרי', tags: ['אקדמיה','משמעת','שיפור'], rank: 16, difficulty: 'מתחילים', timeToValue: 'בינוני', risk: 'נמוך' },
];

const TAGS = ['הכל', 'קריירה', 'מתחילים', 'תיק עבודות', 'פרקטיקה', 'טכני', 'אקדמיה'];

export default function CoursesIndexPage() {
  const router = useRouter();
  const { isDesktop } = useEditorialBreakpoint();
  const [q, setQ] = useState('');
  const [activeTag, setActiveTag] = useState('הכל');

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return CATEGORIES.filter(c => {
      const matchesQ = !s || (c.title + ' ' + c.subtitle + ' ' + c.tags.join(' ')).toLowerCase().includes(s);
      const matchesT = activeTag === 'הכל' || c.tags.includes(activeTag);
      return matchesQ && matchesT;
    }).sort((a, b) => a.rank - b.rank);
  }, [q, activeTag]);

  const handleRead = useCallback((c: Category) => {
    void trackButtonClick('category_read', '/courses', { key: c.key });
    router.push(c.href as any);
  }, [router]);

  const handleChat = useCallback((c: Category) => {
    void trackButtonClick('category_chat', '/courses', { key: c.key });
    router.push({ pathname: '/chat', params: { initialMessage: c.chatSeed, skipWelcome: 'true' } });
  }, [router]);

  return (
    <Paper>
      <AccessibilityButton />
      {IS_WEB && (
        <Head>
          <title>קטגוריות קורסים · Kursi</title>
          <meta name="description" content="16 קטגוריות לימוד. בחרו את הקטגוריה שמעניינת אתכם, וקבלו מדריך בחירה מלא — צ׳ק־ליסט, דגלים אדומים, התאמה אישית." />
          <link rel="canonical" href="https://kursiaii.com/courses" />
        </Head>
      )}

      <ScrollView style={{ flex: 1, backgroundColor: PAPER }} contentContainerStyle={{ backgroundColor: PAPER }}>
        <EditorialHeader />

        <Section py="xl" px="md" max={1240}>
          <PageHero
            kicker="גיליון הקטגוריות · עיתון Kursi"
            title={<>שש עשרה קטגוריות.{'\n'}<Text style={{ color: ACCENT, fontStyle: 'italic' as any }}>בכל אחת — מדריך עורך.</Text></>}
            lede="לכל קטגוריית לימוד אצלנו יש סקירת עורך, צ׳ק־ליסט החלטה, דגלים אדומים ופאק נתונים. אם אתם לא בטוחים בקטגוריה — תתחילו מההתאמה האישית בצ׳אט."
            meta={`${CATEGORIES.length} קטגוריות · ${TAGS.length - 1} תגיות`}
          />

          <View style={s.searchBlock}>
            <Search color={INK} size={18} />
            <TextInput
              value={q}
              onChangeText={setQ}
              placeholder="חיפוש בקטגוריות — תכנות, פסיכומטרי, AI, סייבר…"
              placeholderTextColor={INK_MUTED}
              style={s.searchInput}
              accessibilityLabel="חיפוש קטגוריות"
            />
          </View>

          {/* Tag filter row */}
          <View style={s.tagsRow}>
            {TAGS.map((t) => {
              const active = t === activeTag;
              return (
                <Pressable
                  key={t}
                  onPress={() => setActiveTag(t)}
                  style={({ hovered }: any) => [
                    s.tag,
                    active && s.tagActive,
                    hovered && !active && { borderColor: ACCENT },
                  ]}
                  accessibilityRole="button"
                >
                  <Text style={[s.tagText, active && { color: PAPER }]}>{t}</Text>
                </Pressable>
              );
            })}
          </View>
        </Section>

        {/* Categories list */}
        <Section py="lg" px="md" max={1240}>
          <View style={{ borderTopWidth: 2, borderTopColor: INK }}>
            {filtered.map((c, i) => (
              <View key={c.key} style={s.row}>
                <View style={s.rowHead}>
                  <Numeral size={isDesktop ? 88 : 56}>{String(i + 1).padStart(2, '0')}</Numeral>
                  <View style={{ flex: 1, paddingRight: 24 }}>
                    <View style={{ flexDirection: 'row-reverse', alignItems: 'baseline', gap: 12 } as any}>
                      <Text style={s.rowTitle}>{c.title}</Text>
                      {c.badge ? <Text style={s.badge}>· {c.badge}</Text> : null}
                    </View>
                    <View style={{ height: 8 }} />
                    <Text style={s.rowLede}>{c.subtitle}</Text>
                    <View style={{ height: 14 }} />

                    <View style={s.metaRow}>
                      <View style={s.metaItem}>
                        <Text style={s.metaKey}>רמה</Text>
                        <Text style={s.metaVal}>{c.difficulty}</Text>
                      </View>
                      <View style={s.metaItem}>
                        <Text style={s.metaKey}>זמן לערך</Text>
                        <Text style={s.metaVal}>{c.timeToValue}</Text>
                      </View>
                      <View style={s.metaItem}>
                        <Text style={s.metaKey}>סיכון</Text>
                        <Text style={s.metaVal}>{c.risk}</Text>
                      </View>
                    </View>

                    <View style={{ height: 18 }} />
                    <View style={{ flexDirection: 'row-reverse', flexWrap: 'wrap', gap: 10 } as any}>
                      <EditorialButton label="קראו את המדריך" size="sm" variant="primary" trailing={<ArrowLeft size={12} color={PAPER} />} onPress={() => handleRead(c)} />
                      <EditorialButton label="התאמה אישית" size="sm" variant="secondary" onPress={() => handleChat(c)} />
                    </View>
                  </View>
                </View>
              </View>
            ))}
          </View>

          {filtered.length === 0 && (
            <View style={{ alignItems: 'center', paddingVertical: 60 }}>
              <Eyebrow color={ACCENT}>אין תוצאות</Eyebrow>
              <View style={{ height: 12 }} />
              <Headline level={2} align="center">לא מצאנו קטגוריה כזאת.</Headline>
              <View style={{ height: 14 }} />
              <Text style={[s.rowLede, { textAlign: 'center', maxWidth: 480 }]}>נסו לאפס את הסינון, או דברו עם ה-AI שלנו ישירות.</Text>
              <View style={{ height: 20 }} />
              <EditorialButton label="התחל התאמה" onPress={() => router.push('/chat')} variant="primary" trailing={<ArrowLeft size={14} color={PAPER} />} />
            </View>
          )}
        </Section>

        <Section py="md" px="md" max={1240}><DoubleRule /></Section>

        <Section py="xxl" px="md" max={1100}>
          <View style={{ alignItems: 'center' }}>
            <Eyebrow>קצרה לסבלנות?</Eyebrow>
            <View style={{ height: 14 }} />
            <Headline level={2} align="center">תכתבו לנו מה אתם מחפשים. נחזיר שלוש התאמות.</Headline>
            <View style={{ height: 24 }} />
            <EditorialButton label="התחל התאמה בצ׳אט" onPress={() => router.push('/chat')} variant="primary" size="lg" trailing={<ArrowLeft size={16} color={PAPER} />} />
          </View>
        </Section>

        <EditorialFooter onNavigate={(href) => router.push(href as any)} />
      </ScrollView>
    </Paper>
  );
}

const s = StyleSheet.create({
  searchBlock: {
    flexDirection: 'row-reverse', alignItems: 'center', gap: 12,
    borderWidth: 1, borderColor: INK,
    paddingHorizontal: 18, paddingVertical: 14,
    backgroundColor: PAPER_SOFT, marginTop: 12,
  } as any,
  searchInput: {
    flex: 1, fontFamily: SANS_MED, fontSize: 16, color: INK,
    textAlign: 'right', backgroundColor: 'transparent',
    ...(IS_WEB ? { outlineStyle: 'none' } as any : {}),
  },

  tagsRow: { flexDirection: 'row-reverse', flexWrap: 'wrap', gap: 8, marginTop: 18 } as any,
  tag: {
    paddingVertical: 7, paddingHorizontal: 14,
    borderWidth: 1, borderColor: INK, backgroundColor: 'transparent',
    ...(IS_WEB ? { transition: 'all 160ms ease' } as any : {}),
  },
  tagActive: { backgroundColor: INK },
  tagText: { fontFamily: MONO, fontSize: 11, color: INK, letterSpacing: 1.4, textTransform: 'uppercase' as any },

  row: { paddingVertical: 32, borderBottomWidth: 1, borderBottomColor: INK },
  rowHead: { flexDirection: 'row-reverse', alignItems: 'flex-start' } as any,
  rowTitle: { fontFamily: SERIF_BLACK, fontSize: 36, color: INK, letterSpacing: -1, textAlign: 'right', lineHeight: 40 },
  badge: { fontFamily: MONO, fontSize: 11, color: ACCENT, letterSpacing: 1.6, textTransform: 'uppercase' as any },
  rowLede: { fontFamily: SERIF, fontSize: 18, color: INK_SOFT, lineHeight: 28, textAlign: 'right' },

  metaRow: { flexDirection: 'row-reverse', gap: 24, flexWrap: 'wrap' } as any,
  metaItem: { flexDirection: 'row-reverse', alignItems: 'baseline', gap: 8 } as any,
  metaKey: { fontFamily: MONO, fontSize: 10, color: INK_MUTED, letterSpacing: 1.4, textTransform: 'uppercase' as any },
  metaVal: { fontFamily: SANS_BOLD, fontSize: 13, color: INK },
});
